<template>
	<!-- DEV: to change the dropdown orientation when opened add is-upward class to the dropdown div -->
	<div
		:class="[
			'dropdown',
			isArrowUp ? 'is-upward' : dispatchDropUp ? 'is-upward' : '',
			required ? 'is-required' : '',
			hasError ? 'has-error' : '',
			disabled ? 'is-disabled' : '',
			variant === 'tags' && 'dropdown--tags'
		]"
		:id="uid"
		:variant="variant"
		ref="dropdown"
	>
		<Label
			:for="`${uid}_dd`"
			class="dropdown__label"
			v-if="label"
			:labelText="label"
		/>
		<div
			class="dropdown__input-container"
			ref="dropdownPosRef"
			tabindex="0"
			v-on:keyup="handleKeyUp"
			v-on:keypress.enter.prevent
		>
			<div
				:id="`${uid}_dd`"
				:class="['dropdown__selected', 'itemChecked']"
				@click="toggleMenu()"
				:required="required"
				ref="dropdownSelected"
			>
				<div
					class="dropdown__selected-item"
					v-for="(item, index) in selectedText"
					:key="index"
					ref="selectedItems"
				>
					<TagLabel v-if="variant === 'tags'" :message="item" />
					<span
						ref="selectedItemLabel"
						v-if="variant != 'tags'"
						class="dropdown__selected-item-label"
						>{{ item || defaultValue }}</span
					>
					<span
						class="dropdown__selected-item-separator"
						v-if="
							index < selectedText.length - 1 && variant != 'tags'
						"
						>,</span
					>
				</div>
			</div>
			<div class="dropdown__trailing-container">
				<div class="dropdown__trailing-item" v-show="showIErrorIcon">
					<div class="dropdown__icon dropdown__icon--error">
						<i class="icon icon-error-solid"></i>
					</div>
				</div>
				<div class="dropdown__trailing-item" @click="toggleMenu()">
					<div class="dropdown__icon dropdown__icon--arrow">
						<i
							:class="[
								'icon',
								menuOnTop ? 'icon-caret_up' : 'icon-caret_down'
							]"
						></i>
					</div>
				</div>
			</div>
		</div>

		<DropdownList
			ref="dropdownListRef"
			:autosuggestion="autosuggestion"
			:id="`${uid}_ddl`"
			:isOpen="isOpen"
			:multiSelect="multiSelect"
			:options="items"
			strategy="fixed"
			:value="value"
			v-if="renderList"
			@change="updateValue"
		/>
		<div class="dropdown__text dropdown__text--error" v-if="hasError">
			{{ errorText || defaultErrorText }}
		</div>
		<HelpFeedback
			class="dropdown__text"
			:helpText="helpText"
			v-if="helpText"
			data-test="helpFeedback"
		/>

		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="isOpen"
			strategy="absolute"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
		/>
	</div>
</template>
<script>
//import { ApiService as api } from '@/services/axiosHelper';

import Label from '@/components/forms/label/label';
import dropdownMixins from '@/mixins/dropdown';
import PopperPosition from '@/components/visual/popper-position';
import TagLabel from '@/components/visual/tag-label';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';

export default {
	name: 'DropdownMultiselect',
	inheritAttrs: false,
	mixins: [dropdownMixins],
	model: {
		prop: 'value',
		event: 'change'
	},
	components: {
		Label,
		PopperPosition,
		TagLabel,
		HelpFeedback
	},
	props: {
		toggleList: {
			type: [Function]
		},
		//property for call api and populate items
		api: {
			type: Object,
			default: () => {
				return {
					url: '', //api url
					text: '', //response property that matches "text" property in dropdown
					value: '' //response property that matches "value" property in dropdown
				};
			}
		},
		isArrowUp: {
			type: Boolean,
			default: false
		},
		showIErrorIcon: {
			type: Boolean,
			default: true
		},
		multiSelect: {
			type: Boolean,
			default: true
		},
		variant: {
			type: String,
			default: '',
			validator: value => ['', 'tags'].indexOf(value) !== -1
		}
	},
	data() {
		return {
			dispatchDropUp: null
		};
	},
	computed: {
		selectedText() {
			let text = '';

			if (!this.items.length) {
				return text;
			}

			if (![undefined, null].includes(this.value)) {
				text = this.value;
			}

			return text;
		}
	},
	mounted() {
		document.addEventListener('scroll', this.changeScrollPosition);
		window.addEventListener('resize', this.checkOverflow);
		this.checkOverflow();

		if (this.toggleList instanceof Function) {
			this.toggleMenu = this.toggleList;
		}
	},
	beforeDestroy() {
		document.removeEventListener('scroll', this.changeScrollPosition);
		window.removeEventListener('resize', this.checkOverflow);
	},
	methods: {
		updateValue(val) {
			this.isOpen = false;
			this.$emit('change', val);
		},
		clearValue(index) {
			this.selectedText.splice(index, 1);
			this.$emit('change', this.selectedText);
		},
		changeScrollPosition() {
			this.dispatchDropUp =
				window.innerHeight -
					this.$refs.dropdown.getBoundingClientRect().top <
				300;
		},
		setpopperMinWidth(value) {
			this.popperMinWidth = value;
		},

		checkOverflow() {
			const dropdownSelected = this.$refs.dropdownSelected;
			const selectedItems = this.$refs.selectedItems;
			const selectedItemLabels = this.$refs.selectedItemLabel;

			if (selectedItemLabels && selectedItems) {
				selectedItemLabels.forEach(label =>
					label.classList.remove('dropdown__last-item')
				);

				let totalWidth = 0;
				selectedItems.forEach(label => {
					totalWidth += label.offsetWidth;
				});
				// added an offset of 10px for activate ellipsis early
				if (totalWidth + 10 > dropdownSelected.clientWidth) {
					let lastAffectingLabel;
					let accumulatedWidth = 0;
					for (let label of selectedItems) {
						accumulatedWidth += label.offsetWidth;

						if (
							accumulatedWidth + 10 >=
							dropdownSelected.clientWidth
						) {
							lastAffectingLabel = label;
							break;
						}
					}

					if (lastAffectingLabel) {
						lastAffectingLabel.classList.add('dropdown__last-item');

						//Added a width to visualize the ellipsis
						lastAffectingLabel.style.width = `${lastAffectingLabel.offsetWidth -
							4}px`;
					}
				}
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * Dropdown styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.dropdown {
	position: relative;
	margin: 0;
	font-size: $input-font-size;

	// class for ellipsis
	&__last-item :first-child {
		@include ellipsis;
	}

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__selected {
		width: 100%;

		&-item {
			display: flex;
			align-items: center;
		}

		&.itemChecked {
			display: flex;
			align-items: center;
			flex-direction: row;
		}

		&-item-label {
			display: inline-block;
			line-height: 1;
		}

		&-item-separator {
			margin-right: $gutter-sm;
		}
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;
		z-index: 0;
	}

	&__icon {
		display: flex;
		padding-right: $input-padding-x;
		cursor: pointer;

		&--arrow {
			font-size: 8px;
		}
	}

	&__icon--error {
		display: none;
	}

	select {
		option {
			&:checked {
				background-color: $neutral-4;
			}

			&:hover,
			&:active {
				background-color: lighten($neutral-4, 5%);
			}
		}

		+ .dropdown__trailing-container {
			position: absolute;
			right: $input-padding-x;
			top: $input-padding-y;

			.dropdown__icon--arrow {
				padding-right: 0;
				color: initial;
			}
		}
	}

	&.is-upward {
		.dropdown__icon--arrow {
			.icon {
				transform: rotate(180deg);
			}
		}
		.dropdown__list-container {
			bottom: 100%;
			top: initial;
		}
	}

	&--tags {
		.dropdown {
			&__selected {
				&.itemChecked {
					gap: $gutter-md;
					flex-wrap: wrap;
					height: 100%;
				}
			}

			&__icon {
				align-items: center;
				min-height: 36px;
			}

			&__input-container {
				align-items: flex-start;
			}
		}
	}
}

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.dropdown__input-container {
		border-color: $ui-negative;

		&:focus {
			outline-color: $ui-negative;
		}
	}

	.dropdown__icon--error,
	.dropdown__text--error {
		display: block;
		color: $ui-negative;
	}

	.dropdown__text--error {
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
	}
}
</style>
